import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import cx from 'classnames';
import { HTMLMotionProps, motion } from 'motion/react';
import React from 'react';

export interface AnimatedButtonProps extends HTMLMotionProps<'button'> {
  label: string;
  icon?: React.ElementType;
  iconPosition?: 'left' | 'right';
  variant?: 'primary' | 'outline' | 'link' | 'link-coral';
  active?: boolean;
  fullWidth?: boolean;
  wrapperProps?: HTMLMotionProps<'div'>;
  textClassname?: string;
  iconClassName?: string;
  loading?: boolean;
}

export const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  label,
  icon: Icon,
  iconPosition = 'left',
  variant = 'primary',
  active = false,
  fullWidth = false,
  wrapperProps,
  className,
  textClassname,
  iconClassName,
  loading,
  ...buttonProps
}) => {
  const baseClasses = cx(
    'tw-text-sm tw-font-semibold tw-rounded-xl tw-transition-all active:tw-scale-[0.98] tw-group tw-relative tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-gap-2',
    'disabled:!tw-bg-[var(--color-button-disabled)] disabled:!tw-text-[var(--color-text-weakest)] disabled:!tw-border-transparent disabled:!tw-shadow-none disabled:tw-cursor-not-allowed',
    { 'tw-w-full': fullWidth },
  );

  const variantClasses = {
    primary: cx(
      'tw-px-8 tw-py-4',
      '!tw-bg-[--color-branding-mauve] !tw-text-[--color-text-secondary] hover:!tw-bg-[var(--color-button-hover)] hover:!tw-cursor-pointer',
      'tw-shadow-[var(--effect-button-depth)]',
      'hover:tw-bg-[var(--color-button-hover)] hover:tw-text-[var(--color-text-secondary)] hover:tw-shadow-[var(--effect-inner-depth)]',
      'tw-border-0',
    ),
    outline: cx('tw-px-5 tw-py-4', {
      'tw-border tw-border-transparent tw-bg-[var(--color-button-primary)] tw-text-[var(--color-text-on-accent)] tw-shadow-[var(--effect-inner-depth)]':
        active,
      'tw-bg-transparent tw-text-[var(--color-text-secondary)] tw-border tw-border-solid tw-border-[var(--color-border-button-outline)] hover:!tw-border-transparent hover:tw-bg-[var(--color-button-hover)] hover:tw-shadow-[var(--effect-inner-depth)]':
        !active,
    }),
    link: cx(
      'tw-px-4 tw-py-3',
      'tw-bg-transparent tw-text-[var(--color-text-secondary)]',
      'hover:tw-bg-[var(--color-button-hover)] hover:tw-shadow-[var(--effect-inner-depth)]',
    ),
    'link-coral': cx(
      '!tw-p-0 tw-bg-transparent tw-shadow-none tw-text-[var(--color-secondary)] hover:tw-text-[#d65a43] tw-justify-start tw-w-max tw-border-none',
    ),
  };

  const renderIcon = () => {
    if (!Icon) return null;

    if (loading) {
      return (
        <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />
      );
    }

    let iconColor = 'tw-text-[var(--color-text-secondary)]';
    if (variant === 'primary') {
      iconColor =
        'tw-text-[var(--color-text-on-accent)] group-hover:tw-text-[var(--color-text-secondary)]';
    } else if (variant === 'link-coral') {
      iconColor =
        'tw-text-[var(--color-secondary)] group-hover:tw-text-[#d65a43]';
    }

    return (
      <Icon
        className={cx(
          'tw-w-5 tw-h-5 tw-transition-colors tw-shrink-0',
          iconColor,
          iconClassName,
        )}
      />
    );
  };

  const buttonElement = (
    <motion.button
      type={buttonProps.type || 'button'}
      className={cx(baseClasses, variantClasses[variant], className)}
      {...buttonProps}
      disabled={buttonProps.disabled || loading}
    >
      {iconPosition === 'left' && renderIcon()}
      <span className={cx('tw-transition-colors tw-truncate', textClassname)}>
        {label}
      </span>
      {iconPosition === 'right' && renderIcon()}
    </motion.button>
  );

  if (wrapperProps) {
    return (
      <motion.div
        className={cx('tw-flex', { 'tw-w-full': fullWidth })}
        {...wrapperProps}
      >
        {buttonElement}
      </motion.div>
    );
  }

  return buttonElement;
};
